import { useCallback, useEffect } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  type ExpenseLineItemFormSchema,
  mapFormSchemaToExpenseItemFormSchema,
} from '@webapp/modules/expense/components/ExpenseLineItemForm'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import messages from './messages'
import { useMutateEditExpenseLineItem } from '../../models/useMutateEditExpenseLineItem'

interface UseEditExpenseLineItemPresenterParams {
  item: ExpenseItem
  expenseReportId: number
  onSuccess: () => void
  onSubmittingChange?: (isSubmitting: boolean) => void
}

interface UseEditExpenseLineItemPresenterReturn {
  commands: {
    handleSubmit: (values: ExpenseLineItemFormSchema) => Promise<void>
  }
  states: {
    isSubmitting: boolean
  }
}

export const useEditExpenseLineItemPresenter = ({
  item,
  expenseReportId,
  onSuccess,
  onSubmittingChange,
}: UseEditExpenseLineItemPresenterParams): UseEditExpenseLineItemPresenterReturn => {
  const intl = useProcurifyIntl()
  const { mutate, isLoading } = useMutateEditExpenseLineItem()

  useEffect(() => {
    onSubmittingChange?.(isLoading)
  }, [isLoading, onSubmittingChange])

  const handleSubmit = useCallback(
    async (values: ExpenseLineItemFormSchema) => {
      try {
        const formItem = mapFormSchemaToExpenseItemFormSchema(values, item)
        await mutate({
          itemId: item.id,
          expenseReportId,
          item: formItem,
        })

        window.analytics?.track('PendingExpenseItem Edited', {
          editor: 'approver',
        })

        toast.success({
          title: intl.formatMessage(messages.successTitle),
        })

        onSuccess()
      } catch (err) {
        const errorMessage =
          getMessageFromError(err) ?? intl.formatMessage(messages.errorMessage)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage,
        })
      }
    },
    [item, expenseReportId, intl, mutate, onSuccess]
  )

  return {
    commands: {
      handleSubmit,
    },
    states: {
      isSubmitting: isLoading,
    },
  }
}
